/**
 * ECTRA — UI polish:
 *  - Animated number counters (counts up from 0 when element enters viewport)
 *  - Parallax aurora background (scroll-linked translate via CSS custom prop)
 */
(function () {
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ─────────────────────── Number counters ─────────────────────── */

  const COUNTER_SELECTOR = '[data-ectra-count], .ectra-counter';
  const NUMBER_RE = /^([^\d-]*)([+-]?\d+(?:[.,]\d+)?)(.*)$/;

  function easeOutQuad(t) {
    return 1 - (1 - t) * (1 - t);
  }

  function animateCounter(el) {
    const explicit = el.getAttribute('data-ectra-count');
    const raw = explicit !== null ? explicit : (el.textContent || '').trim();
    const match = raw.match(NUMBER_RE);
    if (!match) return;

    const prefix = match[1] || '';
    const targetStr = match[2];
    const suffix = match[3] || '';
    const target = parseFloat(targetStr.replace(',', '.'));
    if (!isFinite(target)) return;
    const decimals = (targetStr.split(/[.,]/)[1] || '').length;
    const duration = parseInt(el.getAttribute('data-ectra-count-duration'), 10) || 1400;

    if (REDUCED_MOTION) {
      el.textContent = prefix + targetStr + suffix;
      el.classList.add('ectra-counter--done');
      return;
    }

    const start = performance.now();
    function step(now) {
      const progress = Math.min(1, (now - start) / duration);
      const eased = easeOutQuad(progress);
      const value = target * eased;
      const display = decimals > 0 ? value.toFixed(decimals) : Math.round(value).toString();
      el.textContent = prefix + display.replace('.', targetStr.includes(',') ? ',' : '.') + suffix;
      if (progress < 1) {
        requestAnimationFrame(step);
      } else {
        el.textContent = prefix + targetStr + suffix;
        el.classList.add('ectra-counter--done');
      }
    }
    requestAnimationFrame(step);
  }

  function autoMarkCounters(root) {
    /* Heuristic: number-like text inside stat / hero subtitle slots that aren't already marked */
    const candidates = root.querySelectorAll(
      '[class*="stats-band"] [class*="__value"], [class*="trust-highlights"] [class*="__value"], [class*="__stat-value"], [class*="__metric-value"]:not(.engineered-custom__metric-value)'
    );
    candidates.forEach((el) => {
      if (el.classList.contains('ectra-counter') || el.hasAttribute('data-ectra-count')) return;
      const raw = (el.textContent || '').trim();
      if (NUMBER_RE.test(raw)) {
        el.classList.add('ectra-counter');
      }
    });
  }

  function initCounters(root) {
    autoMarkCounters(root);
    const counters = root.querySelectorAll(COUNTER_SELECTOR);
    if (!counters.length) return;

    if (!('IntersectionObserver' in window)) {
      counters.forEach(animateCounter);
      return;
    }

    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            animateCounter(entry.target);
            obs.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.4 }
    );

    counters.forEach((el) => {
      if (el.classList.contains('ectra-counter--done')) return;
      observer.observe(el);
    });
  }

  /* ─────────────────────── Parallax aurora ─────────────────────── */

  let ticking = false;
  let lastScrollY = 0;

  function updateParallax() {
    const docHeight = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
    const progress = Math.max(0, Math.min(1, lastScrollY / docHeight));
    document.documentElement.style.setProperty('--scroll-progress', progress.toFixed(4));
    ticking = false;
  }

  function onScroll() {
    lastScrollY = window.scrollY || window.pageYOffset || 0;
    if (!ticking) {
      requestAnimationFrame(updateParallax);
      ticking = true;
    }
  }

  function initParallax() {
    if (REDUCED_MOTION) return;
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    onScroll();
  }

  /* ─────────────────────── Boot ─────────────────────── */

  function boot() {
    initCounters(document);
    initParallax();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => initCounters(event.target));
  }
})();